import { FC, useMemo } from 'react'
import { CircleMarker, Polygon, Popup, useMap } from 'react-leaflet'
import L, { LatLngExpression, LeafletEventHandlerFnMap } from 'leaflet'
import { GeometryObject, Position } from 'geojson'
import { Poi } from '@/js/types'
import { useAtomValue } from 'jotai'
import { activeGeometryAtom } from '@/js/editor/state/editorUiAtoms.ts'
import { mapDataAtom } from '@/js/editor/state/mapAtoms.ts'
import { useLayerRegistry } from '@/js/editor/components/map/LayerRegistryContext.tsx'

type GeometryProps = {
    poi: Poi
    geometry: GeometryObject
    id: number
}

export type PoiClickedEvent = {
    poi: Poi
    geometry: GeometryObject
}

const toLatLng = (position: Position): LatLngExpression => {
    return [position[1], position[0]]
}

const toPolygonPositions = (coordinates: Position[][]): LatLngExpression[][] => {
    return coordinates.map((ring) => ring.map(toLatLng))
}

export const Geometry: FC<GeometryProps> = ({ poi, geometry, id }) => {
    const { registerLayer, unregisterLayer } = useLayerRegistry()
    const { colors: defaultColors } = useAtomValue(mapDataAtom)
    const leafletMap = useMap()

    const activeGeometry = useAtomValue(activeGeometryAtom)
    const isSelected = activeGeometry === geometry

    const baseColor = useMemo(() => {
        return poi.color ?? defaultColors?.standard
    }, [poi, defaultColors])

    const highlightColor = useMemo(() => {
        return poi.highlight_color ?? defaultColors?.highlight
    }, [poi, defaultColors])

    const displayedColor = isSelected ? highlightColor : baseColor

    const applyHighlight = (layer: L.Layer, highlighted: boolean) => {
        const colorToUse = highlighted ? highlightColor : baseColor
        if (layer instanceof L.CircleMarker || layer instanceof L.GeoJSON) {
            layer.setStyle({ color: colorToUse })
        }
    }

    const map = document.getElementById('mm-map') ?? document.getElementById('mm-editor')

    const customPoiClickEvent = new CustomEvent<PoiClickedEvent>('bsn_poi_clicked', {
        detail: { poi: poi, geometry: geometry },
    })

    const hasPopup = (): boolean => {
        return poi.description?.length > 0
    }

    const includesOneOfAsPart = (searchArray: string[], searchTerms: string[]) => {
        return searchTerms.some((term) => searchArray.some((item) => item.includes(term)))
    }

    const CustomPopupContent = () => {
        const closeOnLeave = (e: { relatedTarget: any } & Event) => {
            const relatedTarget = e.relatedTarget

            const classStringMatches =
                typeof relatedTarget.className === 'string' &&
                includesOneOfAsPart(relatedTarget.className.split(' '), ['leaflet-interactive', 'leaflet-popup'])
            const classObjectMatches =
                (relatedTarget.className?.baseVal ?? false) &&
                relatedTarget.className?.baseVal.split(' ').includes('leaflet-interactive')

            // only close if the mouse did not go back to geometry
            if (!(relatedTarget && relatedTarget instanceof Element && (classStringMatches || classObjectMatches))) {
                leafletMap.closePopup()
            }
        }

        const popup = document.getElementsByClassName('leaflet-popup')
        if (popup[0]) {
            popup[0].addEventListener('mouseleave', closeOnLeave)
        }

        return (
            <>
                <span className={'text-lg'}>{poi.title}</span>
                <p>{poi.description}</p>
            </>
        )
    }

    const eventHandlers: Partial<LeafletEventHandlerFnMap> = {
        click: () => {
            map.dispatchEvent(customPoiClickEvent)
        },
        mouseover: (e) => {
            e.target.openPopup()
            applyHighlight(e.target, true)
        },
        mouseout: (e) => {
            const relatedTarget = e.originalEvent.relatedTarget
            // only close if the mouse did not go into the popup
            if (!(
                relatedTarget &&
                relatedTarget instanceof Element &&
                typeof relatedTarget.className === 'string' &&
                includesOneOfAsPart(relatedTarget.className.split(' '), ['leaflet-popup'])
            )) {
                e.target.closePopup()
            }
            applyHighlight(e.target, isSelected)
        },
    }

    const registerPointLayer = (layer: L.CircleMarker | null) => {
        if (layer) {
            registerLayer(poi.id, id, layer)
        } else {
            unregisterLayer(poi.id, id)
        }
    }

    const registerPolygonLayer = (layer: L.Polygon | null) => {
        if (layer) {
            registerLayer(poi.id, id, layer)
        } else {
            unregisterLayer(poi.id, id)
        }
    }

    if (geometry.type == 'Point') {
        return (
            <CircleMarker
                ref={registerPointLayer}
                center={[geometry.coordinates[1], geometry.coordinates[0]] as LatLngExpression}
                radius={8}
                pathOptions={{
                    color: displayedColor,
                }}
                eventHandlers={eventHandlers}
            >
                {hasPopup() && (
                    <Popup>
                        <CustomPopupContent />
                    </Popup>
                )}
            </CircleMarker>
        )
    }

    if (geometry.type === 'Polygon') {
        return (
            <Polygon
                ref={registerPolygonLayer}
                positions={toPolygonPositions(geometry.coordinates as Position[][])}
                pathOptions={{
                    color: displayedColor,
                }}
                eventHandlers={eventHandlers}
            >
                {hasPopup() && (
                    <Popup>
                        <CustomPopupContent />
                    </Popup>
                )}
            </Polygon>
        )
    }

    return null
}
